import { z } from "zod";

import { PROFILE_CONTRACT_TYPES, PROFILE_CREDIT_TYPES } from "./client-profile-answer.js";

/**
 * `B2d-1` (`P1-DOS-094`, D-1102-11, D-1103-2): **door (2), the dealership starts the assisted dossier.** `POST /api/v1/me/dealership/dossiers` (right `gererInventaire`), answer `201`.
 *
 * The fields are the ratified list (the prototype's `wizIdentity`, in the union of the bank filter's reads and the profile read) plus the three deposit-origin fields the dossier door requires.
 *  - **The salary is a DECLARATION.** It is typed `declaredMonthlyNetSalary` on purpose: it lands in the client's declared profile, labelled as his declaration, and it is NEVER the filter's net salary
 *    (that comes from the bank statements) and is never served back by this door.
 *  - **No code and no token** appear here: the dealer starts the dossier and states what he knows; the client's own agreement is given later, by the client, with his own code (`B2b1`).
 *  - An account already standing on the phone is ATTACHED and its profile is NOT written (`clientAccountExisted: true`): a dealer never overwrites what a client declared.
 * Strict (an extra key is a mismatch) with a lenient READ twin built from the same shapes. Additive only after lane A binds.
 */
export const DEALER_ASSISTED_APPORT_ORIGINS = [
  "epargne_constituee",
  "virement_ou_don_tiers",
  "revenu_activite",
  "vente_bien",
  "autre",
] as const;

function build(object: typeof z.strictObject) {
  const text = z.string();
  const employment = object({
    employer: text,
    position: text,
    contractType: z.enum(PROFILE_CONTRACT_TYPES),
    dateOfHire: text.nullable().optional(),
    declaredSeniorityMonths: z.number().int().nullable().optional(),
    /** The DECLARED net monthly salary, francs. A declaration, never the filter's net salary. */
    declaredMonthlyNetSalary: z.number().int(),
  });
  const commitment = object({
    lenderName: text,
    creditType: z.enum(PROFILE_CREDIT_TYPES),
    outstandingBalance: z.number().int(),
    balanceAsOfDate: text,
    monthlyInstalment: z.number().int(),
    loanStartDate: text.nullable().optional(),
    loanExpectedEndDate: text.nullable().optional(),
  });
  const request = object({
    vehicleId: text.min(1),
    client: object({
      fullName: text.min(1),
      /** The number with its prefix (`+223…`); one account per phone. */
      phone: text.min(1),
      city: text.min(1),
      dateOfBirth: text.nullable().optional(),
      declaredAddress: text.nullable().optional(),
      employments: z.array(employment),
      commitments: z.array(commitment).optional(),
      noCommitmentsDeclared: z.boolean().optional(),
    }),
    financing: object({
      amountFinanced: z.number().int().positive(),
      depositPct: z.number().int(),
      durationMonths: z.number().int(),
      apportOrigine: z.enum(DEALER_ASSISTED_APPORT_ORIGINS),
      apportOrigineDetail: text.nullable().optional(),
      depositOnSalaryAccount: z.boolean(),
    }),
  });
  const answer = object({
    asOf: text,
    dossierId: text,
    reference: text,
    origin: object({ code: z.literal("concession"), words: text.min(1) }),
    client: object({ name: text, phone: text }),
    /** `true` when an account already stood on the phone: attached, profile untouched. */
    clientAccountExisted: z.boolean(),
    /** « En attente de l'accord du client »: the dossier has no consent yet. */
    awaitingConsent: object({ words: text.min(1) }),
  });
  return { request, answer };
}

const strict = build(z.strictObject);
const loose = build(z.looseObject);

/** `POST /api/v1/me/dealership/dossiers`, the request body. */
export const dealerAssistedDossierRequestSchema = strict.request;
/** The 201 answer. Strict. */
export const dealerAssistedDossierAnswerSchema = strict.answer;
/** The dealer client's reading of the answer: unknown keys tolerated, every required field and type identical. */
export const dealerAssistedDossierAnswerReadSchema = loose.answer;
/** The lenient twin of the request, for a stub that reads what a client sent. */
export const dealerAssistedDossierRequestReadSchema = loose.request;

export type DealerAssistedDossierRequest = z.infer<typeof dealerAssistedDossierRequestSchema>;
export type DealerAssistedDossierAnswer = z.infer<typeof dealerAssistedDossierAnswerSchema>;
